import { useState } from "react";
import api from "../../../services/api";
import { currencyMask, displayDate } from "../../../utils/masks";
import { IOrderLot } from "../../../models/IOrderLot";
import OrderLotAddModal from "../order-log-add-modal/OrderLotAddModal";
import TablePage from "../../../containers/table-page";
import { useNavigate, useParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPrint } from "@fortawesome/free-solid-svg-icons";
import { utils } from "../../../utils/utils";
import { toast } from "react-toastify";
import toastOptions from "../../../utils/toastOptions";

type RouteParams = {
  type: string;
}

export default function OrderLotList() {
  const params = useParams<RouteParams>();
  const [refresh, setRefresh] = useState<boolean>(false);
  const [openAddModal, setOpenAddModal] = useState<boolean>(false);
  const [total, setTotal] = useState(0);
  const [pageNo, setPageNo] = useState<number>(1);
  const [ixRunningReport, setIxRunningReport] = useState<number>(-1);
  const navigate = useNavigate();

  const columns = [
    {
      name: "Período",
      sortable: true,
      grow: 2,
      cell: (row: IOrderLot) => (
        <div className="uk-flex uk-flex-column uk-margin-xsmall-y">
          <span className="uk-text-nowrap">{displayDate(row.startAt.toString())} - {displayDate(row.endAt.toString())}</span>
          <span className="uk-text-xsmall uk-text-italic uk-text-meta uk-text-nowrap uk-margin-xsmall-top">Criado em {displayDate(row.createdDate?.toString())}</span>
          <span className="uk-text-xsmall uk-text-italic uk-text-meta uk-text-nowrap">Por {row.createdBy}</span>
        </div>        
      )
    },
    {
      name: "Qtd. Itens",
      sortable: true,
      cell: (row: IOrderLot) => <div>{row.qtyItems}</div>
    },
    {
      name: "Valor Total",
      sortable: true,
      cell: (row: IOrderLot) => <div>{currencyMask(row.amount)}</div>
    },
    {
      name: 'Ações',
      button: true,
      sortable: false,
      cell: (row: IOrderLot) => (
        <div className='uk-flex uk-flex-between'>
          <a href="#" uk-tooltip="title: Gerar Relatório; pos: top" 
            className='uk-margin-small-right'
            onClick={(ev) => { ev.preventDefault(); handleReport(row); }}
          >
            {(ixRunningReport == row.id) ? <div uk-spinner="true; ratio:0.5"></div> : <FontAwesomeIcon icon={faPrint} />}
          </a>
        </div>
      )
    }
  ];

  const fetchOrderLots = async (page: number, limit: number) => {
    try {
      setTotal(0);
      
      const response = await api.get(`/order-lots/${params.type?.toUpperCase()}`, {params: { page, limit }});

      setTotal(response.data.total);

      setPageNo(page);
      return response;
    } catch (e: any) {
      e.messages?.forEach((m: string) => {
        toast.error(m, toastOptions);
      });
      return { data: [], total: 0 };
    }
  };

  const handleAddNew = () => {
    setOpenAddModal(true);
  };

  const afterHandleSubmit = () => {
    setOpenAddModal(false);
    setRefresh(!refresh);
  };

  const afterCloseModal = () => {
    setOpenAddModal(false);
  };

  const handleReport = async(lot: IOrderLot) => {
    setIxRunningReport(lot.id);
    try {
      const { data } = await api.get(`order-lots/${lot?.id}/report`);
      
      const name = `lote-relatório-${new Date().getTime()}.pdf`;
      utils.printPDF(name, data.item);
    } catch (e: any) {
      e.messages?.forEach((m: string) => {
        toast.warning(m, toastOptions);
      });
    } finally {
      setIxRunningReport(-1);
    }
  }

  return (<div id="page-order-lot-list">
    <TablePage
      title={"Lotes de Pedidos Realizados"}
      columns={columns}
      fetch={fetchOrderLots}
      refresh={refresh}
      totalRecords={total}
      labelTotalRecords={`${total} ${total === 1 ? "Lote" : "Lotes"}`}
      messageEmpty={<span>Nenhum <b>lote</b> encontrado!</span>}
      pageNo={pageNo}
      showSubHeader
      subTitle={"Gerencie os lotes de pedidos realizados na plataforma enviados para confecção"}
      showAddButton
      handleAdd={handleAddNew}
      backButtonAction={()=> navigate(-1)}
    />

    <OrderLotAddModal
      open={openAddModal}
      afterHandleSubmit={afterHandleSubmit}
      afterCloseModal={afterCloseModal}
      type={params.type ?? ''}
    />
  </div>);
}